import { useState } from 'react';
import { Search, ChevronRight, ChevronDown, FileText, BookOpen, Mail, Phone, Clock, Tag, HelpCircle } from 'lucide-react';

type FAQCategory = '创建' | '定价' | '故障排查' | '编辑' | '部署' | '时效' | '退款' | '设备';

const categoryColors: Record<FAQCategory, string> = {
  '创建': 'bg-blue-50 text-blue-600',
  '定价': 'bg-purple-50 text-purple-600',
  '故障排查': 'bg-red-50 text-red-600',
  '编辑': 'bg-orange-50 text-orange-600',
  '部署': 'bg-green-50 text-green-600',
  '时效': 'bg-cyan-50 text-cyan-600',
  '退款': 'bg-pink-50 text-pink-600',
  '设备': 'bg-indigo-50 text-indigo-600',
};

const allCategories: FAQCategory[] = ['创建', '定价', '故障排查', '编辑', '部署', '时效', '退款', '设备'];

interface FAQItem {
  category: FAQCategory;
  question: string;
  answer: string;
  updatedAt: string;
}

const faqItems: FAQItem[] = [
  { category: '创建', question: '如何创建舞蹈？', answer: '点击右上角【创建舞蹈】按钮，可以选择上传真人舞蹈视频或使用AI生成舞蹈描述。设置机器人型号和舞蹈名称后，选择生成方式（日常版/专业版）并完成支付即可。', updatedAt: '2026-03-28' },
  { category: '创建', question: 'AI生成舞蹈需要提供什么信息？', answer: '需要提供舞蹈描述（建议包含"全身"关键词以获得更好效果）、期望时长（10-60秒）、机器人型号和舞蹈名称。', updatedAt: '2026-03-25' },
  { category: '定价', question: '日常版和专业版有什么区别？', answer: '日常版一次支付￥100，直接获得仿真视频和舞蹈模型。专业版分两步：先付￥30生成可编辑视频，手动调整关键帧后，满意再付￥70生成精准模型。', updatedAt: '2026-03-28' },
  { category: '定价', question: '支持哪些支付方式？', answer: '目前支持微信支付、支付宝和银联支付三种方式。', updatedAt: '2026-03-20' },
  { category: '故障排查', question: '仿真视频生成失败怎么办？', answer: '在卡片"…"菜单中点击【重试】按钮重新生成。如果多次失败，建议检查视频质量（真人舞蹈、正对镜头、光线明亮、背景简洁）。', updatedAt: '2026-03-27' },
  { category: '故障排查', question: '上传视频一直显示处理中？', answer: '视频处理通常需要1-3分钟，请耐心等待。如果超过5分钟仍未完成，请刷新页面或尝试重新上传。', updatedAt: '2026-03-26' },
  { category: '编辑', question: '如何编辑关键帧？', answer: '仿真视频生成成功后，点击【编辑关键帧】进入专业编辑器。双击时间轴添加关键帧，拖拽调整位置，通过滑杆精细调节每个关节角度，还可以使用预设姿态库。', updatedAt: '2026-03-28' },
  { category: '编辑', question: '关键帧编辑器支持撤销操作吗？', answer: '支持。使用快捷键 Ctrl+Z 可以撤销上一步操作，Ctrl+Shift+Z 可以重做。', updatedAt: '2026-03-22' },
  { category: '部署', question: '如何部署舞蹈到机器人？', answer: '网页版不直接连接机器人。训练完成的舞蹈模型会自动同步到移动端App，在App上连接机器人后选择舞蹈执行即可。', updatedAt: '2026-03-28' },
  { category: '部署', question: '舞蹈模型同步到App需要多久？', answer: '通常在舞蹈生成完成后1-2分钟内自动同步。如果App上没有显示，请检查网络连接并下拉刷新。', updatedAt: '2026-03-24' },
  { category: '时效', question: '仿真视频生成需要多长时间？', answer: '日常版通常需要3-5分钟，专业版视频生成约2-3分钟。实际时间取决于服务器负载情况。', updatedAt: '2026-03-28' },
  { category: '退款', question: '订单支付后可以退款吗？', answer: '订单在"处理中"状态不可退款。如果生成失败或对结果不满意，可以联系客服申请退款，审核通过后将在3-5个工作日内原路退回。', updatedAt: '2026-03-26' },
  { category: '设备', question: '支持哪些机器人型号？', answer: '目前支持 Unitree H2、Unitree R1、智元远征A2、BoosterT1 等主流人形机器人型号。', updatedAt: '2026-03-28' },
  { category: '设备', question: '机器人连接不稳定怎么办？', answer: '请确保机器人和手机在同一Wi-Fi环境下，距离不超过5米。也可以尝试重启机器人和App后重新连接。', updatedAt: '2026-03-23' },
];

const guideDocs = [
  { title: '快速入门指南', desc: '从注册到创建第一个舞蹈的完整流程', icon: '📘', updatedAt: '2026-03-28' },
  { title: '视频上传规范', desc: '拍摄要求、格式说明及常见问题', icon: '📹', updatedAt: '2026-03-27' },
  { title: '关键帧编辑器使用手册', desc: '时间轴操作、关节调节和姿态库详解', icon: '✏️', updatedAt: '2026-03-25' },
  { title: '机器人部署指南', desc: 'App连接、模型同步和执行说明', icon: '🤖', updatedAt: '2026-03-24' },
];

export default function HelpCenter() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<FAQCategory | 'all'>('all');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const filtered = faqItems.filter(item => {
    const matchCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchSearch = !search || item.question.includes(search) || item.answer.includes(search) || item.category.includes(search);
    return matchCategory && matchSearch;
  });

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary font-display">帮助中心</h1>
        <p className="text-sm text-text-secondary mt-1.5">查找文档、常见问题和技术支持</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setExpandedIdx(null); }} placeholder="搜索文档或常见问题..."
          className="w-full pl-11 pr-4 py-3 border border-border-main rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand shadow-soft" />
      </div>

      {/* Guide Documents — Feishu style */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={16} className="text-brand" />
          <h2 className="text-sm font-bold text-text-primary">使用文档</h2>
        </div>
        <div className="bg-white rounded-2xl shadow-soft border border-border-subtle overflow-hidden divide-y divide-border-subtle">
          {guideDocs.map((doc, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/80 transition-colors cursor-pointer group">
              <span className="text-2xl shrink-0">{doc.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary group-hover:text-brand transition-colors">{doc.title}</p>
                <p className="text-xs text-text-muted mt-0.5 truncate">{doc.desc}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex items-center gap-1 text-text-muted">
                  <Clock size={12} />
                  <span className="text-[11px]">{doc.updatedAt}</span>
                </div>
                <ChevronRight size={14} className="text-text-muted group-hover:text-brand transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle size={16} className="text-brand" />
          <h2 className="text-sm font-bold text-text-primary">常见问题</h2>
        </div>

        {/* Category Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => { setActiveCategory('all'); setExpandedIdx(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === 'all' ? 'bg-brand text-white shadow-sm' : 'bg-white text-text-secondary border border-border-subtle hover:border-brand/30'}`}>
            全部
          </button>
          {allCategories.map(cat => (
            <button key={cat} onClick={() => { setActiveCategory(cat); setExpandedIdx(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? categoryColors[cat] : 'bg-white text-text-secondary border border-border-subtle hover:border-brand/30'}`}>
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ List — Feishu doc style */}
        <div className="bg-white rounded-2xl shadow-soft border border-border-subtle overflow-hidden divide-y divide-border-subtle">
          {filtered.map((item, i) => (
            <div key={i}>
              <button onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/80 transition-colors">
                <FileText size={15} className="text-text-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-text-primary">{item.question}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${categoryColors[item.category]}`}>{item.category}</span>
                  </div>
                  {expandedIdx !== i && (
                    <p className="text-xs text-text-muted mt-1 truncate">{item.answer}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] text-text-muted">{item.updatedAt}</span>
                  <ChevronDown size={14} className={`text-text-muted transition-transform duration-200 ${expandedIdx === i ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {expandedIdx === i && (
                <div className="px-5 pb-4 pl-[52px] animate-slide-down">
                  <div className="text-sm text-text-secondary leading-relaxed bg-slate-50 rounded-xl px-4 py-3">
                    {item.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="flex flex-col items-center py-12">
              <Search size={32} className="text-text-muted mb-3" />
              <p className="text-sm text-text-muted">没有找到匹配的结果</p>
              <p className="text-xs text-text-muted mt-1">试试其他关键词或切换分类</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Support */}
      <div className="bg-white rounded-2xl shadow-soft border border-border-subtle p-6">
        <div className="flex items-center gap-2 mb-4">
          <Tag size={16} className="text-brand" />
          <h2 className="text-sm font-bold text-text-primary">技术支持</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-border-subtle">
            <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center shrink-0">
              <Mail size={18} className="text-brand" />
            </div>
            <div>
              <p className="text-xs text-text-muted">邮箱支持</p>
              <p className="text-sm font-semibold text-text-primary">support@odance.com</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-border-subtle">
            <div className="w-10 h-10 bg-warning-50 rounded-xl flex items-center justify-center shrink-0">
              <Phone size={18} className="text-warning" />
            </div>
            <div>
              <p className="text-xs text-text-muted">客服热线</p>
              <p className="text-sm font-semibold text-text-primary">400-888-ODANCE</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-text-muted mt-4 text-center">工作时间：周一至周五 9:00 - 18:00</p>
      </div>
    </div>
  );
}
